import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ALL_LESSONS } from "@/data/lessons";
import { AccountTopBar, mono, serif } from "@/components/AccountLayout";
import { getMyCompletions } from "@/lib/progress.functions";


export const Route = createFileRoute("/my-courses")({
  head: () => {
    const title = "Your courses — WHO KNEW";
    const description =
      "All your WHO KNEW courses and materials in one place. Yours to keep.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/my-courses" },
      ],
    };
  },
  component: CoursesPage,
});

function CoursesPage() {
  const [completed, setCompleted] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getMyCompletions()
        .then((data) => {
          if (cancelled) return;
          if (data && typeof data === "object") {
            setCompleted(new Set(Object.keys(data)));
          }
        })
        .catch(() => {});
    };
    load();
    const onUpdate = () => load();
    window.addEventListener("wk-completion-updated", onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("wk-completion-updated", onUpdate);
    };
  }, []);

  const total = ALL_LESSONS.length;
  const done = ALL_LESSONS.reduce((n, l) => n + (completed.has(l.id) ? 1 : 0), 0);


  return (
    <div className="min-h-screen bg-paper text-ink">
      <AccountTopBar active="courses" />

      <main
        className="pt-[64px] mx-auto"
        style={{ maxWidth: "720px", paddingLeft: "24px", paddingRight: "24px" }}
      >
        <div style={{ paddingTop: "64px" }}>
          <p
            className="font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ ...mono, fontSize: "11px" }}
          >
            Your courses
          </p>
          <h1
            className="text-[28px] md:text-[40px] font-bold leading-[1.15] text-ink"
            style={{ marginTop: "24px" }}
          >
            Your courses and materials.
          </h1>
          <div style={{ marginTop: "24px", width: "60px", height: "2px", backgroundColor: "#8B1E1E" }} />

          <div style={{ marginTop: "48px" }}>
            <p
              className="font-bold uppercase tracking-[0.05em] text-burgundy"
              style={{ ...mono, fontSize: "11px" }}
            >
              A WHO KNEW COURSE
            </p>
            <h2
              className="font-bold text-ink leading-[1.2]"
              style={{ ...serif, fontSize: "28px", fontWeight: 700, marginTop: "12px" }}
            >
              <Link
                to="/my-courses/basic-business-accountancy"
                className="text-link-hover"
                style={{ color: "inherit" }}
              >
                The Basic Business Accountancy Course
              </Link>
            </h2>
            <p
              className="font-bold uppercase tracking-[0.05em]"
              style={{
                ...mono,
                fontSize: "10px",
                marginTop: "8px",
                color: done === total ? "#8B1E1E" : "#5A534A",
              }}
            >
              {done === total
                ? `COMPLETE · ${total} OF ${total} LESSONS`
                : `${done} OF ${total} LESSONS COMPLETE`}
            </p>
            <Link
              to="/my-courses/basic-business-accountancy"
              className="link-hit italic text-burgundy hover:underline decoration-burgundy/40 underline-offset-4 hover:decoration-2 inline-block"
              style={{ ...serif, fontSize: "18px", marginTop: "12px" }}
            >
              View materials
            </Link>
          </div>

          <p
            className="text-center text-[14px] italic"
            style={{ ...serif, color: "#5A534A", lineHeight: 1.5, marginTop: "96px", paddingBottom: "96px" }}
          >
            More courses coming soon.
          </p>
        </div>
      </main>
    </div>
  );
}
